/**
 * Compact metric card -- a label, a big number and an optional hint.
 *
 * Variants:
 *   - 'default' : bordered Card, value uses `color`
 *   - 'hero'    : full-bleed gradient card meant to anchor a page
 *
 * `accent` lets the caller force a colour without going through the
 * MUI palette (e.g. PLATFORM_COLORS.ios for a stat that's ios-specific).
 */

import React from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

const Hero = ({ label, value, hint, accent = '#3357FF' }) => (
  <Card
    sx={{
      background: `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, #ffffff 75%)`,
      borderColor: alpha(accent, 0.35),
      borderWidth: 1.5,
    }}
  >
    <CardContent sx={{ textAlign: 'center', py: { xs: 4, md: 5 } }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: '0.12em' }}
      >
        {label}
      </Typography>
      <Typography
        variant="h2"
        sx={{
          color: accent,
          fontWeight: 800,
          mt: 1,
          lineHeight: 1.05,
          fontSize: { xs: '2.5rem', md: '3.25rem' },
          letterSpacing: '-0.025em',
        }}
      >
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {hint}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const Default = ({ label, value, hint, icon, accent }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        {icon && (
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: accent ? alpha(accent, 0.1) : 'grey.100',
              color: accent || 'text.secondary',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {label}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: accent || 'text.primary',
              fontWeight: 700,
              mt: 0.25,
              wordBreak: 'break-word',
            }}
          >
            {value}
          </Typography>
          {hint && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {hint}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

export default function MetricCard({ variant = 'default', ...rest }) {
  return variant === 'hero' ? <Hero {...rest} /> : <Default {...rest} />;
}
